<script setup lang="ts">
import type { SelectFilter, SelectGroupOption, SelectOption, SelectProps } from 'naive-ui'
import type { TaskOption } from './task-option.type'
import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import { TaskSelectOption, UIcon } from '#components'
import { breakpointsTailwind } from '@vueuse/core'
import { NButton } from 'naive-ui'

const props = defineProps({
  projectId: {
    type: Number as PropType<Project['id'] | null>,
    default: null,
  },
  project: {
    type: Object as PropType<Project<undefined>>,
    default: undefined,
  },
  taskLists: {
    type: Array as PropType<TaskList<'withTasks'>[]>,
    default: () => [],
  },
  selectProps: {
    type: Object as PropType<SelectProps>,
    default: undefined,
  },
  showTaskAddAction: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  taskCreated: [task: Task<undefined>]
}>()

const model = defineModel({
  type: null as unknown as PropType<Task['id'] | Array<Task['id']>>,
  default: undefined,
})

const modelProxy = computed({
  get() {
    if (props.selectProps?.multiple)
      return (model.value as Array<Task['id']>).length ? model.value : [-1]
    return model.value
  },
  set(val) {
    model.value = val
  },
})

const taskOptions = computed(() => {
  const options: Array<SelectOption | SelectGroupOption> = props.taskLists.map<SelectGroupOption & { children: TaskOption[] }>((taskList) => {
    return {
      type: 'group',
      label: taskList.name,
      key: `${taskList.projectId}-${taskList.id}`,
      children: taskList.tasks.map((task) => {
        return {
          label: task.name,
          value: task.id,
          key: `${taskList.projectId}-${taskList.id}-${task.id}`,
          taskListName: taskList.name,
          class: 'task-option',
          task: {
            ...task,
            taskList: {
              ...taskList,
              project: props.project,
            },
          },
        }
      }),
    }
  })

  if (props.selectProps?.multiple) {
    options.unshift({
      label: 'Alle Aufgaben',
      value: -1,
    })
  }

  return options
})

function renderTaskOptionLabel(option: TaskOption | SelectGroupOption) {
  return h(TaskSelectOption, { option })
}

function patternMatched(pattern: string, value: string): boolean {
  try {
    return !!(
      1 + value.toString().toLowerCase().indexOf(pattern.trim().toLowerCase())
    )
  }
  catch {
    return false
  }
}

const filter: SelectFilter = (pattern, option) => {
  const taskOption = option as TaskOption
  const label = taskOption.label
  if (typeof label === 'string')
    return patternMatched(pattern, label) || patternMatched(pattern, taskOption.taskListName)

  return false
}

/* Start of task creation */

const taskNameInput = ref('')

const {
  openTaskCreateModal,
  isTaskCreateModalLoading,
} = useTaskActions()

async function openTaskCreationModal() {
  openTaskCreateModal({
    projectId: props.projectId!,
    taskName: taskNameInput.value,
    taskListsFilterToApply: {
      withCompleted: false,
    },
  }, (task) => {
    emit('taskCreated', task)
  })
}

/* End of task creation */

const { md } = useBreakpoints(breakpointsTailwind)

function onUpdateValue(newVal: number | number[]) {
  if (!Array.isArray(newVal))
    return

  if (newVal.length === 0)
    model.value = [-1]
  else if (newVal[newVal.length - 1] === -1)
    model.value = [-1]
  else if (newVal.length > 1)
    model.value = newVal.filter(taskId => taskId !== -1)
}
</script>

<template>
  <NSelect
    v-bind="selectProps"
    v-model:value="modelProxy"
    filterable
    placeholder="Bitte auswählen"
    :options="taskOptions"
    :filter="filter"
    :render-label="renderTaskOptionLabel"
    :consistent-menu-width="!md"
    class="group/select"
    @search="taskNameInput = $event"
    @update:value="onUpdateValue"
  >
    <template v-if="showTaskAddAction" #action>
      <NButton
        size="small"
        :loading="isTaskCreateModalLoading"
        @click="openTaskCreationModal"
      >
        <template #icon>
          <UIcon name="i-ic-baseline-add" />
        </template>
        Neue Aufgabe hinzufügen
      </NButton>
    </template>
  </NSelect>
</template>

<style scoped>
:global(.task-option .n-base-select-option__content) {
  @apply w-full;
}

.n-select :deep(.copy-button) {
  @apply hidden;
}
</style>
